/**
 * RL-CAN-26: the compact selection transport. A room group's selected set
 * rides as ONE bitset over that group's ordered catalog members (bit i =
 * member i of the group, in catalog order), base64-encoded. Twenty thousand
 * members fit in 2,500 bytes, so the room's initial read carries every mark
 * without a single member id — the ids travel only in the bounded pages the
 * rail actually shows. The bitset is bound to the group's digest by the
 * server; it is display transport, never a receipt: frozen Send and birth
 * receipts keep the exact ordered member ids (RL19–RL21).
 */
export function encodeSelectionBitset(flags: ReadonlyArray<boolean>): string {
  const bytes = new Uint8Array(Math.ceil(flags.length / 8));
  for (let index = 0; index < flags.length; index += 1) {
    if (flags[index]) bytes[index >> 3]! |= 1 << (index & 7);
  }
  return bytesToBase64(bytes);
}

export function decodeSelectionBitset(encoded: string): Uint8Array {
  return base64ToBytes(encoded);
}

/** Bit `index` of a decoded bitset; out-of-range reads are false, never a throw. */
export function selectionBitsetHas(bytes: Uint8Array, index: number): boolean {
  if (!Number.isInteger(index) || index < 0) return false;
  const byte = bytes[index >> 3];
  return byte === undefined ? false : (byte & (1 << (index & 7))) !== 0;
}

/** How many bits of the first `length` are set — the group's selected count, folded from the bitset alone. */
export function selectionBitsetCount(bytes: Uint8Array, length: number): number {
  let count = 0;
  for (let index = 0; index < length; index += 1) {
    if (selectionBitsetHas(bytes, index)) count += 1;
  }
  return count;
}

export type ContextDeliveryMode = "include" | "reference";

/** Delivery choices use the same compact encoding, over the selection's
 * ordered member ids. Unlike catalog marks, this bitmap is part of the
 * selection digest and its immutable message receipt. No duplicate id list. */
export function encodeWorkingContextBitmap(
  memberIds: ReadonlyArray<string>,
  included: ReadonlySet<string>,
): string | undefined {
  if (included.size === 0) return undefined;
  const flags = memberIds.map((memberId) => included.has(memberId));
  if (flags.filter(Boolean).length !== included.size)
    throw new Error("Working knowledge must belong to the selected sources.");
  return encodeSelectionBitset(flags);
}

/** Legacy selections carry references only. Invalid, noncanonical or padded
 * bits refuse instead of silently changing what the person asked to include. */
export function workingContextMemberIds(selection: {
  readonly memberIds: ReadonlyArray<string>;
  readonly workingContextBitmap?: string | undefined;
}): ReadonlyArray<string> | null {
  if (selection.workingContextBitmap === undefined) return [];
  const bytes = decodeSelectionBitset(selection.workingContextBitmap);
  if (bytes.length !== Math.ceil(selection.memberIds.length / 8)) return null;
  const included = selection.memberIds.filter((_, index) => selectionBitsetHas(bytes, index));
  if (
    encodeWorkingContextBitmap(selection.memberIds, new Set(included)) !==
    selection.workingContextBitmap
  )
    return null;
  return included;
}

const BASE64 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

function bytesToBase64(bytes: Uint8Array): string {
  let out = "";
  for (let index = 0; index < bytes.length; index += 3) {
    const a = bytes[index]!;
    const b = bytes[index + 1];
    const c = bytes[index + 2];
    const triple = (a << 16) | ((b ?? 0) << 8) | (c ?? 0);
    out += BASE64[(triple >> 18) & 63]! + BASE64[(triple >> 12) & 63]!;
    out += b === undefined ? "=" : BASE64[(triple >> 6) & 63]!;
    out += c === undefined ? "=" : BASE64[triple & 63]!;
  }
  return out;
}

function base64ToBytes(encoded: string): Uint8Array {
  const clean = encoded.replace(/[^A-Za-z0-9+/]/g, "");
  const bytes = new Uint8Array(Math.floor((clean.length * 3) / 4));
  let cursor = 0;
  for (let index = 0; index < clean.length; index += 4) {
    const values = [0, 1, 2, 3].map((offset) => {
      const char = clean[index + offset];
      return char === undefined ? 0 : BASE64.indexOf(char);
    });
    const triple = (values[0]! << 18) | (values[1]! << 12) | (values[2]! << 6) | values[3]!;
    if (cursor < bytes.length) bytes[cursor++] = (triple >> 16) & 255;
    if (cursor < bytes.length && clean[index + 2] !== undefined)
      bytes[cursor++] = (triple >> 8) & 255;
    if (cursor < bytes.length && clean[index + 3] !== undefined) bytes[cursor++] = triple & 255;
  }
  return bytes;
}
